import React, { useState } from 'react';
import { Mail, MessageSquare, LifeBuoy, Send, CheckCircle } from 'lucide-react';
import LegalPageLayout from './LegalPageLayout';

// NOTE: Draft — swap in your real support email + hours when ready.
const SUPPORT_EMAIL = 'hello@goldentips.app';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    // Client-only stub: open the user's email client pre-filled. Replace
    // with a real submission endpoint (e.g., Resend / SendGrid) if / when
    // you wire one up.
    const subject = encodeURIComponent(`Golden Tips — message from ${form.name || 'guest'}`);
    const body = encodeURIComponent(
      `From: ${form.name} <${form.email}>\n\n${form.message}`
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <LegalPageLayout
      title="Contact us"
      subtitle="We reply within one business day for account and payout issues, and within a few days for everything else."
    >
      <section className="grid md:grid-cols-3 gap-4 not-prose">
        <div className="stat-card" data-testid="contact-support-card">
          <Mail size={20} className="text-[#F5C542] mb-2" />
          <h3 className="font-heading font-semibold mb-1">Support email</h3>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm text-[#F5C542] hover:underline break-all">
            {SUPPORT_EMAIL}
          </a>
        </div>
        <div className="stat-card" data-testid="contact-hours-card">
          <MessageSquare size={20} className="text-[#F5C542] mb-2" />
          <h3 className="font-heading font-semibold mb-1">Hours</h3>
          <p className="text-sm text-[#A1A1AA]">Mon–Fri · 9am–6pm ET</p>
        </div>
        <div className="stat-card" data-testid="contact-priority-card">
          <LifeBuoy size={20} className="text-[#F5C542] mb-2" />
          <h3 className="font-heading font-semibold mb-1">Payout issues</h3>
          <p className="text-sm text-[#A1A1AA]">Include your slug — we&apos;ll jump on it.</p>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-heading font-semibold text-white mb-3">Send us a message</h2>
        {sent ? (
          <div className="rounded-xl bg-green-500/10 border border-green-500/30 p-5 flex items-start gap-3" data-testid="contact-sent">
            <CheckCircle size={22} className="text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-green-100">Draft opened in your email app</p>
              <p className="text-sm text-green-200/80 mt-1">
                If nothing opened, email us directly at{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a>.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#52525B]">Your name</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#F5C542] focus:outline-none"
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#52525B]">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#F5C542] focus:outline-none"
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#52525B]">Message</label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#F5C542] focus:outline-none resize-y"
                data-testid="contact-message-input"
              />
            </div>
            <button
              type="submit"
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="contact-submit"
            >
              <Send size={16} /> Open in email
            </button>
            <p className="text-xs text-[#52525B]">
              This opens a pre-filled draft in your email app. For faster support, include your account
              email and (if relevant) your public slug.
            </p>
          </form>
        )}
      </section>
    </LegalPageLayout>
  );
}
